import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import { loginUser, getCurrentUser } from '../../api/auth';
import { createNewChatSession } from '../../api/chat';
import { handleError } from '../../utils/errorHandling';
import InputField from '../../components/common/InputField';
import Button from '../../components/common/Button';
import {
  AuthPage,
  AuthCard,
  AuthForm,
  AuthRememberLabel,
  SecondaryLink,
  ErrorText,
  LogoImage,
  CopyrightText,
  AuthCta,
} from './AuthStyles';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  const handleForgotPassword = () => {
    toast('Za pomoč se obrnite na podporo na support@smartgifty.com', {
      icon: '🛡️',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      // 1. Get JWT token
      const data = await loginUser(username.trim(), password);

      // 2. Set token in axios instance
      useAuthStore.getState().setToken(data.access);

      // 3. Get full user data (including is_staff)
      const userData = await getCurrentUser();

      // 4. Login with complete user data
      login(userData, data.access);

      // 5. Ustvari novo sejo chata takoj ob prijavi
      try {
        const newSession = await createNewChatSession();
        sessionStorage.setItem('supernova_chat_session_id', newSession.id.toString());
      } catch (chatErr) {
        console.error('Failed to create initial chat session', chatErr);
        // Če ne uspe, počisti staro sejo
        sessionStorage.removeItem('supernova_chat_session_id');
      }

      navigate('/chat');
    } catch (err: any) {
      const message = handleError(err, 'Login');
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = Boolean(username.trim() && password);

  return (
    <AuthPage>
      <AuthCard>
        <LogoImage src="/assets/sn-logo.svg" alt="Logo" />

        {errorMessage && (
          <ErrorText role="status" aria-live="assertive">
            {errorMessage}
          </ErrorText>
        )}

        <AuthForm onSubmit={handleSubmit} noValidate>
          <InputField
            id="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            placeholder="Username or email"
            autoComplete="username"
            label="Username or email"
            hideLabel
          />
          <InputField
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Password"
            autoComplete="current-password"
            label="Password"
            hideLabel
          />
          <AuthRememberLabel>
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
            />
            Keep me logged in
          </AuthRememberLabel>
          <Button type="submit" disabled={isSubmitting || !isFormValid}>
            {isSubmitting ? 'Signing in…' : 'Login'}
          </Button>
        </AuthForm>

        <AuthCta>
          <SecondaryLink to="#" onClick={handleForgotPassword}>
            Forgot your password?
          </SecondaryLink>
        </AuthCta>
      </AuthCard>

      <CopyrightText>Copyright 2025 SmartGifty. All Rights Reserved.</CopyrightText>
    </AuthPage>
  );
};

export default Login;
